import React, { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { CONSTANTS } from '../data.js';
import { iconFor, getCardStats } from '../lib/utils';
import { ASSETS } from '../assetManifest.js';

type DeckBuilderProps = {
  onClose: (saved: boolean) => void;
  discovered: string[];
  initialDeck: string[];
  onSaveDeck: (deck: string[]) => void;
  currentEraKey: string;
  unlockedEras: string[];
};

export const DeckBuilder: React.FC<DeckBuilderProps> = ({
  onClose,
  discovered,
  initialDeck,
  onSaveDeck,
  currentEraKey,
  unlockedEras,
}) => {
  const [deck, setDeck] = useState<string[]>(initialDeck);

  const deckCounts = useMemo(() => {
    return deck.reduce<Record<string, number>>((acc, key) => {
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [deck]);

  const sortedDiscovered = useMemo(() => {
    return [...discovered].sort((a, b) => CONSTANTS.elements[a].name.localeCompare(CONSTANTS.elements[b].name));
  }, [discovered]);

  const addCard = useCallback((key: string) => {
    const count = deckCounts[key] || 0;
    if (count < CONSTANTS.deckBuilding.maxCardCopies && deck.length < CONSTANTS.deckBuilding.deckSizeMax) {
      setDeck(prev => [...prev, key]);
    }
  }, [deck.length, deckCounts]);

  const removeCard = useCallback((key: string) => {
    const index = deck.lastIndexOf(key);
    if (index > -1) {
      const newDeck = [...deck];
      newDeck.splice(index, 1);
      setDeck(newDeck);
    }
  }, [deck]);

  const isDeckValid = deck.length >= CONSTANTS.deckBuilding.deckSizeMin && deck.length <= CONSTANTS.deckBuilding.deckSizeMax;

  const handleSave = () => {
    if (isDeckValid) {
      onSaveDeck(deck);
      onClose(true);
    }
  };

  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center z-50"
      style={{ background: 'rgba(26, 22, 17, 0.85)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="relative flex flex-col p-8 w-[1000px] max-w-[95vw] h-[85vh] rounded-xl"
        style={{
          background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, ${CONSTANTS.ui.theme.colors.background} 100%)`,
          border: `2px solid ${CONSTANTS.ui.theme.colors.border}`,
          boxShadow: CONSTANTS.ui.theme.effects.ornateBoxShadow
        }}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex justify-between items-center mb-6 flex-shrink-0">
          <h2 className="font-cinzel text-4xl font-bold" style={{ color: CONSTANTS.ui.theme.colors.primary, textShadow: CONSTANTS.ui.theme.effects.textGlow }}>
            Deck Builder
          </h2>
          <button onClick={() => onClose(false)} className="font-crimson px-6 py-2 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95" style={{ background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary} 0%, ${CONSTANTS.ui.theme.colors.accent} 100%)`, color: CONSTANTS.ui.theme.colors.background }}>
            Close
          </button>
        </div>

        <div className="flex-grow grid grid-cols-1 md:grid-cols-3 gap-6 overflow-hidden">
          {/* Card Pool */}
          <div className="md:col-span-2 overflow-y-auto pr-4 space-y-2" data-tutorial-target="deck_builder_available_cards">
            <h3 className="font-cinzel text-2xl font-bold sticky top-0 py-2" style={{ color: CONSTANTS.ui.theme.colors.primary, background: `linear-gradient(to bottom, ${CONSTANTS.ui.theme.colors.surface} 80%, transparent)` }}>
              Available Cards
            </h3>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
              {sortedDiscovered.map(key => {
                const element = CONSTANTS.elements[key];
                const isLocked = !unlockedEras.includes(element.era);
                const stats = getCardStats({ id: key, elementKey: key }, currentEraKey);
                const categoryKey = element.category || 'materials';
                const categoryInfo = CONSTANTS.ui.library.categories.find(c => c.key === categoryKey);
                const frameAssetKey = categoryInfo?.frameAsset || 'default';
                const framePath = ASSETS.card_frames?.[frameAssetKey]?.path;
                const categoryColor = categoryInfo?.color || CONSTANTS.ui.theme.colors.border;
                const countInDeck = deckCounts[key] || 0;
                const canAdd = !isLocked && countInDeck < CONSTANTS.deckBuilding.maxCardCopies && deck.length < CONSTANTS.deckBuilding.deckSizeMax;

                return (
                  <button 
                    key={key} 
                    onClick={() => addCard(key)} 
                    disabled={!canAdd} 
                    className="relative w-full aspect-[2/3] rounded-md p-1.5 flex flex-col bg-cover bg-center transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105"
                    style={{
                      backgroundImage: framePath ? `url(${framePath})` : `linear-gradient(145deg, ${CONSTANTS.ui.theme.colors.surface} 0%, ${CONSTANTS.ui.theme.colors.background} 100%)`,
                      border: `1px solid ${categoryColor}`,
                      boxShadow: `0 2px 4px rgba(0,0,0,0.2), 0 0 3px ${categoryColor}30`
                    }}
                  >
                    {isLocked && (
                      <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded-md z-10">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-lock text-white/50"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      </div>
                    )}
                    <div className="flex justify-between items-start text-left">
                      <span className="font-cinzel text-[10px] font-bold text-white/90 leading-tight line-clamp-2 flex-1">{element.name}</span>
                      <span className="font-bold text-sm ml-1" style={{color: CONSTANTS.ui.theme.colors.primary}}>{stats.power}</span>
                    </div>
                    <div className="w-full flex-grow my-1 bg-black/20 rounded-sm flex items-center justify-center overflow-hidden">
                      <img src={iconFor(key)} alt={element.name} className="w-full h-full object-contain p-1" />
                    </div>
                    <div className="font-crimson text-[10px] text-center" style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}>
                      In Deck: {countInDeck}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Current Deck */}
          <div className="flex flex-col overflow-hidden" data-tutorial-target="deck_builder_current_deck">
            <h3 className="font-cinzel text-2xl font-bold mb-2 flex-shrink-0" style={{ color: CONSTANTS.ui.theme.colors.primary }}>
              Your Deck
            </h3>
            <div className="flex-grow overflow-y-auto pr-4 space-y-2">
              {Object.entries(deckCounts).sort(([keyA], [keyB]) => CONSTANTS.elements[keyA].name.localeCompare(CONSTANTS.elements[keyB].name)).map(([key, count]) => {
                const element = CONSTANTS.elements[key];
                return (
                  <div key={key} onClick={() => removeCard(key)} className="flex items-center gap-3 p-2 rounded-lg cursor-pointer" style={{ background: CONSTANTS.ui.theme.colors.background, border: `1px solid ${CONSTANTS.ui.theme.colors.border}` }}>
                    <img src={iconFor(key)} alt={element.name} className="w-8 h-8 object-contain" />
                    <span className="font-crimson text-sm flex-grow">{element.name}</span>
                    <span className="font-crimson text-sm font-bold">x{count}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex-shrink-0 mt-4" data-tutorial-target="deck_builder_deck_stats">
              <div className={`font-cinzel text-lg font-bold text-center mb-2 ${isDeckValid ? 'text-green-400' : 'text-red-400'}`}>
                {deck.length} / {CONSTANTS.deckBuilding.deckSizeMax}
              </div>
              <p className="font-crimson text-xs text-center mb-4" style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}>
                Deck must have {CONSTANTS.deckBuilding.deckSizeMin}-{CONSTANTS.deckBuilding.deckSizeMax} cards.
              </p>
              <button onClick={handleSave} disabled={!isDeckValid} className="font-crimson w-full px-8 py-3 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.success} 0%, #32cd32 100%)`, color: CONSTANTS.ui.theme.colors.background }}>
                Save Deck
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
